import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess(false);

    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to send password reset email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-8 overflow-y-auto">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center fade-in">
          <button
            onClick={() => navigate('/login')}
            className="absolute left-4 top-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={24} />
          </button>
          <img
            src="/ChefLogo.jpg"
            alt="CheF"
            className="w-[120px] h-[120px] rounded-full object-cover mx-auto mb-4 shadow-md ring-[3px] ring-[#FF9500]"
          />
          <h2 className="text-4xl font-bold gradient-text mb-2 text-center">Reset Password</h2>
          <p className="text-gray-600 text-lg">
            Enter your email to receive a password reset link
          </p>
        </div>

        <div className="card p-8 slide-up">
          {success ? (
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle size={40} className="text-green-600" />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Check Your Email
                </h3>
                <p className="text-gray-600">
                  We've sent a password reset link to <strong>{email}</strong>. 
                  Please check your inbox and follow the instructions.
                </p>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="w-full btn-primary py-4 text-lg font-bold"
              >
                Back to Login
              </button>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-base font-bold text-gray-900 mb-2">
                  📧 Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail size={20} className="text-orange-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="modern-input w-full pl-12 pr-4 py-4 text-lg"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              {error && (
                <div className="error-card p-4">
                  <div className="error-icon">
                    <AlertCircle size={24} />
                  </div>
                  <p className="font-medium">{error}</p>
                </div>
              )}

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn-primary py-4 text-lg font-bold shadow-2xl hover:shadow-orange-200/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="loading-spinner mr-3"></div>
                      <span>Sending...</span>
                    </div>
                  ) : (
                    <span>Send Reset Link</span>
                  )}
                </button>
              </div>

              <div className="text-center pt-4">
                <Link to="/login" className="text-red-600 hover:text-red-700 font-medium">
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>

        {/* Food decoration */}
        <div className="text-center text-4xl opacity-40 pb-8">
          🍔🍟🥤🍗🍕
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
